/**
 * @module Login Service
 */

import {popup} from '@acng/frontend-discovery';
import {guard} from '@acng/frontend-rubicon';
import {NULLABLE, OBJECT, OPTIONAL, STRING} from '@acng/frontend-rubicon';

import {get, post} from '../../core/service/backend.js';
import {inject} from '../../core/service/ng';
import {t} from '../../locale/config/translate.js';
import {AUTH_USER_DATA} from '../model/user.js';

const url = 'user/login';

/**
 * @param {Element} [element]
 *
 * @returns {Promise<void>}
 */
const restore = async (element) => {
  inject('director').setWait();

  const data = await get('user/auth-user', element);
  ASSERT: guard(data, AUTH_USER_DATA);

  inject('$rootScope').$broadcast('userPool.login.success', data);
};

/**
 * Perform a user login.
 *
 * @param {HTMLElement} element
 * @param {Valid<REQUEST>} credentials
 */
const login = async (element, credentials) => {
  const onsOverlay = inject('onsOverlay');
  const overlay = onsOverlay.get('pageLogin');

  try {
    const authUserData = await post(url, credentials, element, async (error) => {
      switch (error.data.code) {
        case 1007:
          onsOverlay.create('confirm-device').open();
          return null;
        case 1009:
          popup(element).warn(await t('userPool.reactivationMailSent'));
          return null;
      }

      if (!onsOverlay.count()) {
        onsOverlay.create('pageLogin').disableBackdrop().open();
      }

      throw error;
    });

    ASSERT: guard(authUserData, RESPONSE, `decoded ${url} response body`);

    if (authUserData) {
      inject('$rootScope').$broadcast('userPool.login.success', authUserData);
    }

    overlay?.close();

    return authUserData;
  } catch (reason) {
    // No further treatment is necessary.
  }
};

/**
 * @param {unknown} data
 *
 * @returns {asserts data is Valid<REQUEST>}
 */
const valid = (data) => guard(data, REQUEST, `decoded ${url} request body`);

/**
 * Validate the login request body.
 */
const REQUEST = /* @__PURE__ */ OBJECT({
  login: STRING,
  password: STRING,
  remember: /* @__PURE__ */ OPTIONAL(STRING),
});

/**
 * Validate the decoded login response body.
 */
const RESPONSE = /* @__PURE__ */ NULLABLE(AUTH_USER_DATA);

export {valid, login, restore};
